/**
 * Вкладка «Сметы» карточки проекта: текущие версии смет проекта с переходом в карточку сметы и в раздел
 * «Сметы» (поток 2). Данные — `GET /estimates?projectId`, суммы по уровню денег «Акты и счета».
 */
import {
  ESTIMATE_KINDS,
  ESTIMATE_VERSION_KINDS,
  type EstimateSummary,
  type ProjectDetail,
  seesMoney,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowRight, ListTree } from 'lucide-react';
import { tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';
import { StatusBadge } from '../estimates/EstimatesPage';
import { estimatesQuery } from '../estimates/lib';

const linkClass =
  'inline-flex min-h-11 items-center gap-1.5 text-[14px] font-medium text-ink underline-offset-4 hover:underline md:min-h-0';

export function EstimatesTab({ project }: { project: ProjectDetail }) {
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const q = useQuery(estimatesQuery({ projectId: project.id }));
  const groups = new Map<string, EstimateSummary[]>();
  for (const e of q.data ?? []) {
    const k = e.kind;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(e);
  }
  const order: Array<keyof typeof ESTIMATE_KINDS> = ['summary', 'object', 'local'];
  const actions = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <Link to="/estimates" search={{ projectId: project.id }} className={linkClass}>
        Раздел «Сметы» <ArrowRight className="size-4" aria-hidden />
      </Link>
      <Link to="/estimates/resources" search={{ projectId: project.id }} className={linkClass}>
        <ListTree className="size-4" aria-hidden /> Ресурсы по сметам
      </Link>
    </div>
  );
  if (q.isPending) return <Spinner label="Загрузка смет" />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data?.length) {
    return (
      <Panel title="Сметы" actions={actions}>
        <EmptyState title="Смет у проекта пока нет">
          Смета загружается из файла АВС или KENML в разделе «Сметы» — там же сравнение версий и привязка к
          бюджету.
        </EmptyState>
      </Panel>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {order
        .filter((k) => groups.has(k))
        .map((k) => {
          const items = groups.get(k)!;
          return (
            <Panel
              key={k}
              title={ESTIMATE_KINDS[k]}
              meta={`${items.length} ${items.length === 1 ? 'смета' : items.length < 5 ? 'сметы' : 'смет'}`}
              actions={k === order.find((o) => groups.has(o)) ? actions : undefined}
              bodyClassName="p-0"
            >
              <ul className="divide-y divide-line">
                {items.map((e) => (
                  <li key={e.id}>
                    <Link
                      to="/estimates/$estimateId"
                      params={{ estimateId: e.id }}
                      className="flex min-h-11 flex-col gap-1 px-5 py-3 hover:bg-surface-2 md:flex-row md:items-center md:gap-4"
                    >
                      <span className="num w-[110px] shrink-0 text-[13px] text-ink-2">{e.code}</span>
                      <span className="min-w-0 flex-1 text-[14px] font-medium text-ink [overflow-wrap:anywhere]">
                        {e.name}
                        {e.objectName ? (
                          <span className="font-normal text-ink-2"> — {e.objectName}</span>
                        ) : null}
                      </span>
                      <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                        <span>
                          v{e.versionNo} · {ESTIMATE_VERSION_KINDS[e.versionKind]}
                        </span>
                        {e.hasIssues ? <Badge tone="warning">расхождения</Badge> : null}
                        <StatusBadge status={e.status} />
                      </span>
                      <span
                        className={clsx(
                          'num shrink-0 text-right text-[14px] md:w-[160px]',
                          money ? 'text-ink' : 'text-ink-2',
                        )}
                      >
                        {money ? tenge(e.totals.amount) : 'суммы скрыты'}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          );
        })}
    </div>
  );
}
